import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { creditsAPI } from '@/lib/api';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setError('No payment session found');
      return;
    }

    // Poll for payment status
    let attempts = 0;
    const maxAttempts = 10;
    
    const checkPaymentStatus = async () => {
      try {
        const response = await creditsAPI.getPaymentStatus(sessionId);
        const data = response.data;
        
        if (data.status === 'completed') {
          setStatus('success');
          setPaymentData(data);
          return true;
        } else if (data.payment_status === 'paid') {
          // Payment complete but status update in progress
          setStatus('success');
          setPaymentData(data);
          return true;
        }
        return false;
      } catch (err) {
        console.error('Error checking payment status:', err);
        if (attempts >= maxAttempts - 1) {
          setStatus('error');
          setError('Unable to verify payment. Please contact support.');
        }
        return false;
      }
    };

    const pollStatus = async () => {
      const completed = await checkPaymentStatus();
      if (!completed && attempts < maxAttempts) {
        attempts++;
        setTimeout(pollStatus, 2000); // Poll every 2 seconds
      }
    };

    pollStatus();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
      <Card className="bg-white/10 backdrop-blur-sm border-white/20 max-w-md w-full">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-purple-400 animate-spin mx-auto mb-4" />
              <CardTitle className="text-white text-2xl">Processing Payment...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <CardTitle className="text-white text-2xl">Payment Successful!</CardTitle>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <CardTitle className="text-white text-2xl">Payment Error</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'checking' && (
            <p className="text-gray-300">Please wait while we confirm your payment...</p>
          )}
          
          {status === 'success' && paymentData && (
            <>
              <div className="p-4 bg-green-500/20 rounded-lg border border-green-500/30">
                <p className="text-green-300 text-lg font-semibold">
                  +{paymentData.credits_added} Credits Added!
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Total Balance: {paymentData.total_credits} credits
                </p>
              </div>
              <p className="text-gray-300">{paymentData.message}</p>
            </>
          )}
          
          {status === 'error' && (
            <p className="text-red-300">{error}</p>
          )}
          
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-purple-600 hover:bg-purple-700 mt-4"
            data-testid="go-to-dashboard-btn"
          >
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
